import Constants from '../../globals/Constants';
import {fabric} from 'fabric-webpack';

const minSize = 15;
const halfAngle = 180;
const point = 10;
const radToDeg = halfAngle / Math.PI;

const calcAngle = (startCoords: any, endCoords: any): number => {
   return Math.atan2(endCoords.y - startCoords.y, endCoords.x - startCoords.x) * radToDeg;
};

const initializeEventListeners = (canvas: any): void => {
   const scaling = (event: any): void => {
      if (event.target.shapeType !== Constants.annotation.type.arrow) {
         return;
      }

      const arrow = event.target;
      const mouse = canvas.getPointer(event.e, true);

      if (arrow.originX === 'left' && arrow.__corner === 'ml') {
         const newOrigins = arrow.getPointByOrigin('right', 'center');
         arrow.left = newOrigins.x;
         arrow.top = newOrigins.y;
         arrow.originX = 'right';
      }

      const pathConst = 6;
      arrow.dirty = true;
      arrow.width = Math.max(arrow.width * arrow.scaleX, minSize);
      arrow.path[pathConst][1] = arrow.width;
      arrow.pathOffset.x = arrow.width / 2;
      arrow.scaleX = 1;
      arrow.scaleY = 1;
      arrow.setCoords();

      if (arrow.__corner === 'ml') {
         const angle = calcAngle(mouse, arrow.oCoords.mr);
         arrow.setAngle(angle);
      } else if (arrow.__corner === 'mr') {
         const angle = calcAngle(arrow.oCoords.ml, mouse);
         arrow.setAngle(angle);
      }

      arrow.perPixelTargetFind = true;
      arrow.targetFindTolerance = 10;
      canvas.setActiveObject(arrow);
   };

   const modified = (event: any): void => {
      canvas.off('mouse:up');

      if (event.target.shapeType !== Constants.annotation.type.arrow) {
         return;
      }

      const arrow = event.target;

      arrow.width = arrow.width * arrow.scaleX;
      arrow.height = arrow.height * arrow.scaleY;
      arrow.scaleX = 1;
      arrow.scaleY = 1;

      if (arrow.originX === 'right') {
         const newOrigins = arrow.getPointByOrigin('left', 'center');
         arrow.left = newOrigins.x;
         arrow.top = newOrigins.y;
         arrow.originX = 'left';
      }

      arrow.perPixelTargetFind = true;
      arrow.targetFindTolerance = 10;
      canvas.setActiveObject(arrow);
   };

   canvas.on('object:scaling', scaling);
   canvas.on('object:modified', modified);
};

const turnOnMouseEventListeners = (canvas: any, color: any): void => {
   let started = false;
   let x = 0;
   let y = 0;
   canvas.selection = true;
   turnOffMouseEventListeners(canvas);

   const mouseDown = (event: any): void => {
      if (canvas.getActiveObject() && canvas.getActiveObject().width > minSize) {
         return;
      }

      const mouse = canvas.getPointer(event.e);
      x = mouse.x;
      y = mouse.y;
      started = true;
      canvas.on('mouse:up', mouseUp);

      const arrow = `M ${point},${point} ${point},0 0,${point} ${point},${point * 2} ${point},${point} Z L 0,${point}`;
      const path = new fabric.Path(arrow, {
         originY: 'center',
         originX: 'left',
         flipX: true,
         width: 0,
         height: 0,
         left: x,
         top: y,
         strokeWidth: Constants.annotation.strokeWidth * canvas.strokeScale,
         stroke: color,
         fill: color,
         lockScalingFlip: true,
         padding: Constants.annotation.controlsPadding,
         shapeType: Constants.annotation.type.arrow,
         centeredRotation: false,
         perPixelTargetFind: true,
         targetFindTolerance: 10
      });

      path.setControlsVisibility({bl: false, br: false, mb: false, ml: true, mr: true, mt: false, tl: false, tr: false, mtr: false});

      canvas.add(path);
      canvas.setActiveObject(path);
   };

   const mouseMove = (event: any): void => {
      if (!started) {
         return;
      }

      const arrow = canvas.getActiveObject();
      const mouse = canvas.getPointer(event.e);
      const angle = calcAngle({x: x, y: y}, mouse);
      const width = mouse.x - x;
      const height = mouse.y - y;
      const distance = Math.sqrt(Math.pow(width, 2) + Math.pow(height, 2)) * arrow.scaleX;
      const pathConst = 6;

      arrow.set({width: distance});
      arrow.set('visible', true);
      arrow.path[pathConst][1] = distance;
      arrow.pathOffset.x = arrow.width / 2;
      arrow.scaleX = 1;
      arrow.scaleY = 1;
      arrow && arrow.setCoords();
      arrow.setAngle(angle);
      canvas.selection = false;
      canvas.setActiveObject(arrow);
      canvas.renderAll();
   };

   const mouseUp = (): void => {
      const arrow = canvas.getActiveObject();

      if (arrow && arrow.width < minSize) {
         canvas.remove(arrow);
      }

      started = false;
      canvas.selection = true;
      canvas.trigger('object:added');
      canvas.deactivateAll();
      canvas.renderAll();
      canvas.off('mouse:up');
   };

   canvas.defaultCursor = 'crosshair';
   canvas.on('mouse:down', mouseDown);
   canvas.on('mouse:move', mouseMove);
   canvas.on('mouse:up', mouseUp);
};

const turnOffMouseEventListeners = (canvas: any): void => {
   canvas.defaultCursor = 'default';
   canvas.off('mouse:down');
   canvas.off('mouse:move');
   canvas.off('mouse:up');
};

export default {
   turnOffMouseEventListeners,
   turnOnMouseEventListeners,
   initializeEventListeners
};